import ApiUtils from "utils/ApiUtil";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import type { FundDetailsListInterface } from "pages/FundManagement/FundDetails";
import type { mmTransactionInterface } from "pages/FundManagement/Transaction";
import { fundManagementApiUrls } from "constants/apiEndPoints";
import QueryParamsBuilder from "components/helpers/commonFunctions/QueryParamsBuilder";
import { convertToISOFormat } from "utils/GeneralUtil";
import { exportExcelFileNames } from "constants/constantText";


const viewAllFundDetails = async (page: number, size: number): Promise<unknown> => await
    new Promise((resolve, reject) => {
        ApiUtils.getWithToken(`${fundManagementApiUrls.viewAllFundDetailsUrl}?page=${page}&size=${size}`)
            .then((funds) => {
                resolve(funds)
            }).catch((error) => { reject(error); })

    });

const viewAllTransactionDetails = async (page: number, size: number, sortBy: string,
    sortOrder: string, searchValue: string, filters: unknown): Promise<mmTransactionInterface[]> => {

    let url = QueryParamsBuilder({ baseUrl: fundManagementApiUrls.viewAllTransactionDetailsUrl, page, size, sortBy, sortOrder, searchValue });

    const filterParams = new URLSearchParams();
    if (filters) {
        if (filters.startDate) {
            filterParams.append('startDate', filters.startDate);
        }

        if (filters.endDate) {
            filterParams.append('endDate', filters.endDate);
        }

        if (filters.fStartDate) {
            filterParams.append('fStartDate', convertToISOFormat(filters.fStartDate));
        }

        if (filters.fEndDate) {
            filterParams.append('fEndDate', convertToISOFormat(filters.fEndDate));
        }

        if (filters.sStartDate) {
            filterParams.append('sStartDate', convertToISOFormat(filters.sStartDate));
        }

        if (filters.sEndDate) {
            filterParams.append('sEndDate', convertToISOFormat(filters.sEndDate));
        }

        if (filters.fundType) {
            const fundType = filters.fundType || '';
            filterParams.append('fundType', fundType);
        }

        if (filters.status) {
            const status = filters.status || '';
            filterParams.append('status', status);
        }
        if (filters.orderType) {
            const orderType = filters.orderType || '';
            filterParams.append('orderType', orderType);
        }
    }
    if (filterParams.toString()) {
        url += `&${filterParams.toString()}`;
    }
    const funds = await ApiUtils.getWithToken(url) as mmTransactionInterface[];
    return funds
}

const exportFundDetails = async (complaints: FundDetailsListInterface[]): Promise<void> => {
    try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Fund Management Report');

        const fundData = complaints.map((funds) => ({
            'Fund Name': funds.description,
            'Total Units': Number(Number(funds.totalUnits).toFixed(7)),
            'Symbol': funds.symbol,
            'Contract ID': funds.contractId,
            'Value of the Fund': Number((Number(funds?.todayPrice) * Number(funds?.totalUnits)).toFixed(7)),
            'Client Units': Number(Number(funds.clientUnits).toFixed(7)),
            'ZP Units': Number(Number(funds.mmUnits).toFixed(7)),
            'Current Price': Number(Number(funds.todayPrice).toFixed(2)),

        }));

        // File headers
        const columns = [
            { header: 'Fund Name', key: 'Fund Name', width: 70 },
            { header: 'Total Units', key: 'Total Units', width: 30 },
            { header: 'Symbol', key: 'Symbol', width: 15 },
            { header: 'Contract ID', key: 'Contract ID', width: 15 },
            { header: 'Value of the Fund', key: 'Value of the Fund', width: 25 },
            { header: 'Client Units', key: 'Client Units', width: 25 },
            { header: 'ZP Units', key: 'ZP Units', width: 30 },
            { header: 'Current Price', key: 'Current Price', width: 15 },
        ];

        worksheet.columns = columns;
        worksheet.addRows(fundData);

        // Header style
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = {
                bold: true,
            };
            cell.alignment = {
                horizontal: 'center',
                vertical: 'middle',
            };
        });

        // Data style
        const dataRows = worksheet.getRows(2, fundData.length);
        if (dataRows) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dataRows.forEach((row: { eachCell: (arg0: (cell: any) => void) => void; }) => {
                row.eachCell((cell) => {
                    cell.font = {
                        color: { argb: '000000' },
                    };
                    cell.alignment = {
                        horizontal: 'center',
                        vertical: 'middle',
                    };

                    if (cell.isHyperlink) {
                        const hyperlink = cell.value;
                        if (
                            hyperlink
                            && typeof hyperlink === 'object'
                            && 'text' in hyperlink
                            && 'hyperlink' in hyperlink
                        ) {
                            cell.value = {
                                text: hyperlink.text,
                                hyperlink: hyperlink.hyperlink,
                            };
                            cell.style = {
                                font: { color: { argb: '0563C1' }, underline: 'single' },
                            };
                            cell.alignment = {
                                horizontal: 'center',
                                vertical: 'middle',
                            };
                        }
                    }
                });
            });
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const dataBlob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(dataBlob, exportExcelFileNames.FUND_DETAILS);
    } catch (err) {
        console.log(err);
    }
}

const getDailyFundPrice = async (page: number, size: number, sortBy: string,
    sortOrder: string, searchValue: string): Promise<unknown> => await
        new Promise((resolve, reject) => {
            const url = QueryParamsBuilder({ baseUrl: fundManagementApiUrls.getDailyFundPrice, page, size, sortBy, sortOrder, searchValue });
            ApiUtils.getWithToken(url)
                .then((funds) => {
                    resolve(funds)
                }).catch((error) => { reject(error); })

        });

const getUnitAllocationInfoByOrderId = async (orderId: number, page: number, size: number): Promise<unknown> =>
    await new Promise((resolve, reject) => {
        const url = QueryParamsBuilder({ id: orderId, baseUrl: fundManagementApiUrls.getUnitAllocationInfoByOrderId, page, size })
        ApiUtils.getWithToken(url)
            .then((info) => {
                resolve(info)
            }).catch((error) => { reject(error); })
    })
export {
    viewAllFundDetails,
    viewAllTransactionDetails,
    exportFundDetails,
    getDailyFundPrice,
    getUnitAllocationInfoByOrderId
}